import JSONApiService from "@/services/JSONApiService";
import APIService from "@/services/APIService";
import {
  INTERVIEWER,
  AVAILABLE_INTERVIEW_SLOT,
  nested,
  include,
  PARTICIPANTS,
  USER,
  INTERVIEW_SLOTS,
  BOOKING,
  LOCATION,
  FEEDBACKS,
  SCORES,
  CRITERIA,
  COMMENTS,
  AUTHOR,
  FEEDBACK_CRITERIA
} from "@/models/relationships";
import { INTERVIEW_FORMAT_TEMPLATE_MODEL } from "@/models/interview-format-template-model";
import { INTERVIEW_FORMAT_MODEL } from "@/models/interview-format-model";
import {
  INTERVIEW_FORMAT_SELF_SCHEDULE_SLOT,
  SELF_SCHEDULE_SELECT_SLOT
} from "@/models/self-schedule-slot-model";
import { INTERVIEW_SLOT_MODEL } from "@/models/interview-slot-model";
import { INTERVIEW_FEEDBACK_MODEL } from "@/models/interview-feedback-model";
import { INTERVIEW_PARTICIPANT_MODEL } from "@/models/interview-participant-model";
import { disablePluralizeForCall } from "@/services/utils";
import { RESCHEDULE_SLOT_MODEL } from "@/models/reschedule-slot-model";
import { INTERVIEW_INVITATION_MODEL } from "@/models/interview-invitation-model";
import { SELF_SCHEDULE_INVITE_MODEL } from "@/models/self-schedule-invite-model";
import pluralize from "pluralize";

const interviewSlotIncludes = include(
  INTERVIEW_FORMAT_MODEL,
  LOCATION,
  nested(INTERVIEW_FORMAT_MODEL, BOOKING),
  nested(FEEDBACKS, SCORES, CRITERIA),
  nested(FEEDBACKS, INTERVIEWER),
  nested(PARTICIPANTS, USER),
  nested(PARTICIPANTS, COMMENTS),
  nested(COMMENTS, AUTHOR)
);

const interviewApi = commit => ({
  getInterviewTemplates: async () =>
    JSONApiService(commit)
      .all(INTERVIEW_FORMAT_TEMPLATE_MODEL)
      .get(),
  getInterviewers: async interviewFormatId =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(INTERVIEWER)
      .get(),
  getInterviewFormat: async (interviewFormatId, params) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .get(params),
  getBookingInterviewFormats: async (bookingId, withInclude) => {
    return JSONApiService(commit)
      .one(BOOKING, bookingId)
      .all(INTERVIEW_FORMAT_MODEL)
      .get(withInclude && include(nested(INTERVIEW_SLOTS, PARTICIPANTS, USER)));
  },
  getInterviewFormatAvailableSlots: async (interviewFormatId, params) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(AVAILABLE_INTERVIEW_SLOT)
      .get(params),
  getInterviewFormatFeedbackCriteria: async interviewFormatId =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(FEEDBACK_CRITERIA)
      .get(),
  createInterviewSlot: async (interviewSlot, interviewFormatId) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(INTERVIEW_SLOT_MODEL)
      .post(interviewSlot),
  createInterviewFeedback: async interviewFeedback =>
    JSONApiService(commit)
      .all(INTERVIEW_FEEDBACK_MODEL)
      .post(interviewFeedback),
  editInterviewFeedback: async (data, id) =>
    JSONApiService(commit)
      .one(INTERVIEW_FEEDBACK_MODEL, id)
      .patch(data),
  getInterviewSlots: async params =>
    JSONApiService(commit)
      .all(INTERVIEW_SLOT_MODEL)
      .get({
        ...params,
        ...include(
          nested(INTERVIEW_FORMAT_MODEL, BOOKING),
          nested(PARTICIPANTS, USER)
        )
      }),
  getInterviewSlot: async id =>
    JSONApiService(commit)
      .one(INTERVIEW_SLOT_MODEL, id)
      .get(interviewSlotIncludes),
  createSelfScheduleSlots: async (data, interviewFormatId) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(INTERVIEW_FORMAT_SELF_SCHEDULE_SLOT)
      .post(data),
  rescheduleSlot: async (data, interviewFormatId) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(RESCHEDULE_SLOT_MODEL)
      .post(data),
  createSelfScheduleSlotParticipant: async (interviewFormatId, params) => {
    const api = JSONApiService(commit);
    api.one(INTERVIEW_FORMAT_MODEL, interviewFormatId);
    disablePluralizeForCall(api, () => {
      api.all(SELF_SCHEDULE_SELECT_SLOT);
    });
    return api.post({}, params);
  },
  getSelfScheduleSlots: async interviewFormatId =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(INTERVIEW_FORMAT_SELF_SCHEDULE_SLOT)
      .get(),
  editInterviewFormat: async (interviewDetails, id) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, id)
      .patch(interviewDetails),
  editSelfScheduleSlots: async (data, interviewFormatId) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(INTERVIEW_FORMAT_SELF_SCHEDULE_SLOT)
      .patch(data),
  editInterviewParticipant: async ({ interviewId, participantId, data }) =>
    JSONApiService(commit)
      .one(INTERVIEW_SLOTS, interviewId)
      .one(INTERVIEW_PARTICIPANT_MODEL, participantId)
      .patch(data),
  editInterviewSlot: async (id, data) =>
    JSONApiService(commit)
      .one(INTERVIEW_SLOT_MODEL, id)
      .patch(data, interviewSlotIncludes),
  getInterviewInvitations: async params =>
    JSONApiService(commit)
      .all(INTERVIEW_INVITATION_MODEL)
      .get({
        ...params,
        ...include(INTERVIEW_SLOT_MODEL, USER, INTERVIEW_FORMAT_MODEL)
      }),
  createSelfScheduleInvite: async (interviewFormatId, data) =>
    JSONApiService(commit)
      .one(INTERVIEW_FORMAT_MODEL, interviewFormatId)
      .all(SELF_SCHEDULE_INVITE_MODEL)
      .post(data),
  getInterviewFormatFiles: async ({ interviewFormatId, collection }) =>
    APIService.client().get(
      `${pluralize(
        INTERVIEW_FORMAT_MODEL
      )}/${interviewFormatId}/file/${collection}`
    ),
  addInterviewFormat: async data =>
    JSONApiService(commit)
      .all(INTERVIEW_FORMAT_MODEL)
      .post(data)
});

export default interviewApi;
